import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Award, Target, Brain, CheckCircle2, ArrowUpRight, ArrowRight, Layers, GitBranch, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#2563EB', '#10B981', '#7C3AED', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#8B5CF6'];

const AccuracyBar = ({ label, accuracy, total, corrected }) => (
  <div className="mb-3" data-testid={`accuracy-bar-${label}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm font-medium text-slate-700 truncate max-w-[200px]">{label}</span>
      <div className="flex items-center gap-3 text-xs">
        <span className="text-slate-500">{total} records</span>
        {corrected > 0 && <span className="text-amber-600">{corrected} corrected</span>}
        <span className={`font-semibold ${accuracy >= 90 ? 'text-green-600' : accuracy >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
          {accuracy}%
        </span>
      </div>
    </div>
    <div className="w-full bg-slate-100 rounded-full h-2.5">
      <div
        className={`h-2.5 rounded-full transition-all duration-700 ${
          accuracy >= 90 ? 'bg-green-500' : accuracy >= 70 ? 'bg-amber-500' : 'bg-red-500'
        }`}
        style={{ width: `${Math.max(accuracy, 2)}%` }}
      />
    </div>
  </div>
);

const FlowItem = ({ flow, count }) => {
  const parts = flow.split(' -> ');
  return (
    <div className="flex items-center gap-2 py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-sm font-medium truncate max-w-[140px]">{parts[0]}</span>
      <ArrowRight size={12} className="text-slate-400 flex-shrink-0" />
      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-sm font-medium truncate max-w-[140px]">{parts[1]}</span>
      <span className="text-xs text-slate-400 ml-auto flex-shrink-0">{count}x</span>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3 shadow-lg text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
          {entry.name}: <span className="font-semibold">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export const MLInsights = ({ token }) => {
  const [impact, setImpact] = useState(null);
  const [insights, setInsights] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [impactRes, insightsRes, correctionsRes] = await Promise.all([
        axios.get(`${API}/ml/impact-analysis`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ml/correction-insights`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/spend/corrections`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setImpact(impactRes.data);
      setInsights(insightsRes.data);
      setCorrections(correctionsRes.data.corrections || []);
    } catch (err) {
      console.error('Failed to fetch ML data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (!impact?.has_data) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="ml-insights-empty">
        <div className="text-center">
          <Brain className="mx-auto mb-4 text-slate-300" size={48} />
          <p className="text-slate-600 text-lg font-medium">No data yet</p>
          <p className="text-sm text-slate-500 mt-2">Upload and classify data, then submit corrections to see impact metrics.</p>
        </div>
      </div>
    );
  }

  const o = impact.overall;
  const hasCorrections = o.total_corrections > 0;

  return (
    <div className="space-y-6" data-testid="ml-insights-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Correction Impact Tracker</h1>
        <p className="text-slate-600">See how your corrections are improving AI classification accuracy</p>
      </div>

      {/* Impact Score Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="impact-cards">
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-blue-50 p-2 rounded-sm"><CheckCircle2 size={18} className="text-blue-600" /></div>
          </div>
          <p className="text-xs text-slate-500 mb-1">Total Corrections</p>
          <p className="text-2xl font-heading font-bold text-[#0F172A]" data-testid="metric-total-corrections">{o.total_corrections}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-green-50 p-2 rounded-sm"><TrendingUp size={18} className="text-green-600" /></div>
          </div>
          <p className="text-xs text-slate-500 mb-1">Accuracy Improvement</p>
          <p className="text-2xl font-heading font-bold" data-testid="metric-accuracy-improvement">
            <span className={o.accuracy_improvement > 0 ? 'text-green-600' : 'text-slate-400'}>
              {o.accuracy_improvement > 0 ? '+' : ''}{o.accuracy_improvement}%
            </span>
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-purple-50 p-2 rounded-sm"><Target size={18} className="text-purple-600" /></div>
          </div>
          <p className="text-xs text-slate-500 mb-1">Current Accuracy</p>
          <p className="text-2xl font-heading font-bold text-[#0F172A]" data-testid="metric-current-accuracy">{o.adjusted_accuracy}%</p>
          <p className="text-xs text-slate-400 mt-1">was {o.avg_raw_confidence}%</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-amber-50 p-2 rounded-sm"><Layers size={18} className="text-amber-600" /></div>
          </div>
          <p className="text-xs text-slate-500 mb-1">Similar Records Improved</p>
          <p className="text-2xl font-heading font-bold text-[#0F172A]" data-testid="metric-similar-improved">~{o.similar_records_improved}</p>
        </div>
      </div>

      {/* Accuracy by Category + Correction Flows */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6" data-testid="category-accuracy">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">Accuracy by Category</h2>
          <p className="text-xs text-slate-500 mb-4">Classification accuracy per category after corrections</p>
          {impact.category_accuracy.length > 0 ? (
            <div>
              {impact.category_accuracy.map((cat) => (
                <AccuracyBar
                  key={cat.category}
                  label={cat.category}
                  accuracy={cat.accuracy}
                  total={cat.total}
                  corrected={cat.corrected}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">No classified data yet</p>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="correction-flows">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">Top Correction Patterns</h2>
          <p className="text-xs text-slate-500 mb-4">Most common reclassifications</p>
          {impact.top_correction_flows.length > 0 ? (
            <div>
              {impact.top_correction_flows.map((f, i) => (
                <FlowItem key={i} flow={f.flow} count={f.count} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <GitBranch className="mx-auto mb-2 text-slate-300" size={24} />
              <p className="text-sm text-slate-400">No correction patterns yet</p>
            </div>
          )}

          {hasCorrections && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>Category changes</span>
                <span className="font-semibold">{impact.correction_types.category_changes}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 mt-1">
                <span>Supplier corrections</span>
                <span className="font-semibold">{impact.correction_types.supplier_changes}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Chart */}
      {impact.timeline.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="correction-timeline">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">Correction Activity</h2>
          <p className="text-xs text-slate-500 mb-4">Daily corrections and cumulative total over the last 30 days</p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={impact.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" stroke="#94A3B8" style={{ fontSize: '11px' }} />
              <YAxis yAxisId="left" stroke="#94A3B8" style={{ fontSize: '11px' }} />
              <YAxis yAxisId="right" orientation="right" stroke="#94A3B8" style={{ fontSize: '11px' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar yAxisId="left" dataKey="daily" fill="#2563EB" name="Daily Corrections" radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="cumulative" stroke="#10B981" strokeWidth={2} name="Cumulative" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Category Accuracy Bar Chart */}
      {impact.category_accuracy.length > 2 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="accuracy-chart">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">AI Confidence vs Accuracy</h2>
          <p className="text-xs text-slate-500 mb-4">Comparing AI confidence scores with actual accuracy per category</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={impact.category_accuracy} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" domain={[0, 100]} stroke="#94A3B8" style={{ fontSize: '11px' }} />
              <YAxis type="category" dataKey="category" width={120} stroke="#94A3B8" style={{ fontSize: '11px' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="avg_confidence" fill="#2563EB" name="AI Confidence %" radius={[0, 3, 3, 0]} />
              <Bar dataKey="accuracy" fill="#10B981" name="Accuracy %" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Corrections Table */}
      {corrections.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="recent-corrections">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-4">Recent Corrections</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Original</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Corrected To</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Confidence</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {corrections.slice(0, 10).map((c, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 line-through">{c.original_category || '-'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600">{c.corrected_category || '-'}</td>
                    <td className="px-4 py-3 text-sm"><span className="font-mono">{c.confidence_rating}/10</span></td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{c.feedback_notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Impact Summary Banner */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1e3a5f] text-white rounded-sm p-6" data-testid="impact-summary">
        <h3 className="text-lg font-heading font-semibold mb-3">Your Correction Impact</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-blue-200 mb-1">Data Quality</p>
            <p className="text-2xl font-heading font-bold">
              {insights?.data_quality_contribution?.toFixed(1) || 0}%
            </p>
          </div>
          <div>
            <p className="text-sm text-blue-200 mb-1">Accuracy Boost</p>
            <p className="text-2xl font-heading font-bold text-green-400">
              +{insights?.accuracy_boost?.toFixed(1) || 0}%
            </p>
          </div>
          <div>
            <p className="text-sm text-blue-200 mb-1">Categories Improved</p>
            <p className="text-2xl font-heading font-bold">
              {insights?.unique_categories_corrected || 0}
            </p>
          </div>
          <div>
            <p className="text-sm text-blue-200 mb-1">Future Records Benefit</p>
            <p className="text-2xl font-heading font-bold text-amber-400">
              ~{insights?.future_improvements || 0}
            </p>
          </div>
        </div>
        <p className="text-sm text-blue-200 mt-4">
          Each correction trains the AI to make better decisions on similar transactions. Keep correcting to maximize accuracy.
        </p>
      </div>
    </div>
  );
};
